import { Capacitor } from '@capacitor/core';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle, 
  Shield, 
  Key, 
  Clock,
  RefreshCw,
  Send,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import React, { useState } from 'react';
import { resolveB2cFrontendUrl } from '../../shared/seo/siteOrigin';
import { useTranslation } from 'react-i18next';

import { supabase } from '../../lib/supabase';
import { logEvent } from '../../services/loggingService';
import { SEO } from '../../shared/components/SEO';
import { GGJLogoFull } from '../Brand/GGJLogo';

interface ForgotPasswordPageProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'privacy-policy' | 'cookie-policy' | 'terms-of-service' | 'gdpr-compliance' | 'payment-success') => void;
}

type ResetMethod = 'email-link' | 'otp';
type FlowStep = 'method-selection' | 'email-input' | 'otp-input' | 'success';

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resetMethod, setResetMethod] = useState<ResetMethod>('otp'); // Default to OTP (recommended)
  const [currentStep, setCurrentStep] = useState<FlowStep>('method-selection');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [otpTimeLeft, setOtpTimeLeft] = useState(0);

  // OTP countdown timer
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (otpTimeLeft > 0) {
      interval = window.setInterval(() => {
        setOtpTimeLeft(prev => {
          if (prev <= 1) {
            setError('OTP has expired. Please request a new one.');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpTimeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleBackToHome = () => {
    onNavigate?.('home');
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError(t('authForgotPassword.enterEmail'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (resetMethod === 'email-link') {
        // Traditional email link method
        console.log('Inserting password reset tracking record for email:', email.trim());
        
        try {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('user_id')
            .eq('email', email.trim())
            .maybeSingle();
          
          console.log('User lookup result:', { found: !!userData, error: userError?.message });
          
          const trackingData = {
            user_id: userData?.user_id || null,
            email: email.trim(),
            otp_code: 'EMAIL_LINK_' + Math.random().toString(36).substring(2, 15),
            expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
            used: false
          };
          
          const { error: insertError } = await supabase
            .from('password_reset_otps')
            .insert(trackingData);
          
          if (insertError) {
            console.error('Failed to insert password reset tracking:', insertError);
          }
        } catch (trackingError) {
          console.error('Error in tracking logic:', trackingError);
        }
        
        const currentOrigin = resolveB2cFrontendUrl();
        const redirectUrl = `${currentOrigin}/reset-password`;
        
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: redirectUrl,
        });

        if (error) throw error;
        
        setSuccess(t('authForgotPassword.checkEmailLink'));
        setCurrentStep('success');
        
        await logEvent({
          level: 'info',
          action: 'password_reset_link_requested',
          details: { email, method: 'email-link' }
        });
      } else {
        // OTP method (Recommended)
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-otp-password-reset`;
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || t('authForgotPassword.failedSendOtp'));
        }

        setCurrentStep('otp-input');
        setOtpTimeLeft(5 * 60); // 5 minutes
        setSuccess(t('authForgotPassword.securityCodeSent'));
        
        await logEvent({
          level: 'info',
          action: 'password_reset_otp_requested',
          details: { email, method: 'otp' }
        });
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      setError(error.message || t('authForgotPassword.failedSendReset'));
      
      await logEvent({
        level: 'error',
        action: 'password_reset_failed',
        details: { email, method: resetMethod, error: error.message }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (otpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    if (otpTimeLeft === 0) {
      setError(t('authForgotPassword.otpExpired'));
      return;
    }

    if (!email.trim()) {
      setError(t('authForgotPassword.emailMissing'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-otp-password-reset`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: email.trim(), 
          otp: otpCode.trim() 
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('authForgotPassword.invalidExpiredCode'));
      }

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${resolveB2cFrontendUrl()}/auth/confirm`
      });

      if (resetError) {
        throw new Error(resetError.message || t('authForgotPassword.failedSendReset'));
      }

      setSuccess(t('authForgotPassword.codeVerified'));
      setCurrentStep('success');
      
      await logEvent({
        level: 'info',
        action: 'password_reset_otp_verified',
        details: { email }
      });

      if (data.resetToken) {
        setTimeout(() => {
          onNavigate?.('reset-password');
        }, 2000);
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);
      setError(error.message || t('authForgotPassword.invalidExpiredCodeRetry'));
      
      await logEvent({
        level: 'error',
        action: 'password_reset_otp_verification_failed',
        details: { email, error: error.message }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-otp-password-reset`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend OTP');
      }

      setOtpTimeLeft(5 * 60);
      setOtpCode('');
      setSuccess(t('authForgotPassword.newCodeSent'));
      
      setTimeout(() => setSuccess(null), 3000);
      
      await logEvent({
        level: 'info',
        action: 'password_reset_otp_resent',
        details: { email }
      });
    } catch (error: any) {
      console.error('Resend OTP error:', error);
      setError(error.message || t('authForgotPassword.failedResend'));
      
      await logEvent({
        level: 'error',
        action: 'password_reset_otp_resend_failed',
        details: { email, error: error.message }
      });
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setCurrentStep('method-selection');
    setEmail('');
    setOtpCode('');
    setError(null);
    setSuccess(null);
    setOtpTimeLeft(0);
    setResetMethod('otp');
  };

  return (
    <div className="min-h-screen max-h-screen bg-gray-950 flex p-0 lg:p-6 gap-0 lg:gap-4">
      <SEO
        title="Forgot Password — Reset Your GoGrabJob Account"
        description="Reset your GoGrabJob password securely via email link or one-time code."
        canonical="https://www.gograbjob.com/forgot-password"
        noIndex
      />
      {/* Left Panel - Mascot & Branding (Dripfy Style) - ROUNDED CARD */}
      <div className="hidden lg:flex lg:w-[42%] bg-gradient-to-br from-[#1e2638] to-[#1a1f35] rounded-2xl shadow-2xl border border-gray-800/30 relative overflow-hidden flex-col justify-start p-8">
        {/* Logo - CLICKABLE */}
        <button
          onClick={handleBackToHome}
          className="flex items-center mb-3 hover:opacity-80 transition-opacity"
        >
          <GGJLogoFull iconSize={52} fontSize={26} />
        </button>

        {/* Content */}
        <div className="flex-1 flex flex-col justify-center items-center text-center mt-8">
          {/* Mascot */}
          <motion.img
            key={currentStep}
            src="/images/GoGrabJob Forgot Password Mascot.png"
            alt="GoGrabJob Forgot Password Mascot"
            className="w-full max-w-xs h-auto object-contain mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          />
          
          {/* Text */}
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-3 leading-tight">
            {currentStep === 'success' ? t('views.forgotPassword.checkEmail') : t('views.forgotPassword.title')}
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed">
            {currentStep === 'success' 
              ? t('authForgotPassword.subtitleSuccess') 
              : t('authForgotPassword.subtitleMethod')}
          </p>
        </div>
      </div>

      {/* Right Panel - Reset Form (Dripfy Style) - ROUNDED CARD; native: theme layered card */}
      <div
        className="w-full lg:w-[58%] flex items-center justify-center p-6 lg:py-8 lg:px-16 relative overflow-y-auto"
        style={
          isNative
            ? {
                marginTop: -12,
                borderRadius: '24px 24px 0 0',
                background: 'var(--ggj-mobile-content-card-bg, var(--vscode-bg-primary))',
                borderTop: 'var(--ggj-mobile-content-card-border-top, none)',
                boxShadow: 'var(--ggj-mobile-content-card-shadow, none)',
                minHeight: '100%',
              }
            : { background: '#141824' }
        }
      >
        {/* Mobile Logo - CLICKABLE */}
        <div className="lg:hidden absolute top-6 left-6">
          <button 
            onClick={handleBackToHome}
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <GGJLogoFull iconSize={38} fontSize={19} />
          </button>
        </div>

        {/* Form Container */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-xl mt-16 lg:mt-0 my-auto"
        >
          <AnimatePresence mode="wait">
            {/* Step 1: Method Selection */}
            {currentStep === 'method-selection' && (
              <motion.div
                key="method-selection"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-white mb-2">
                    {t('authForgotPassword.resetPasswordHeading')}
                  </h2>
                  <p className="text-gray-400">
                    {t('authForgotPassword.chooseMethod')}
                  </p>
                </div>

                {/* Method Options */}
                <div className="space-y-3 mb-6">
                  {/* OTP Option (Recommended) */}
                  <label className="cursor-pointer block">
                    <input
                      type="radio"
                      name="resetMethod"
                      value="otp"
                      checked={resetMethod === 'otp'}
                      onChange={(e) => setResetMethod(e.target.value as ResetMethod)}
                      className="sr-only"
                    />
                    <div className={`p-4 rounded-lg border-2 transition-all ${
                      resetMethod === 'otp' 
                        ? 'border-[#8b5cf6] bg-[#8b5cf6]/10' 
                        : 'border-gray-700 bg-[#1a1f35] hover:border-gray-600'
                    }`}>
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-[#8b5cf6]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Key className="w-5 h-5 text-[#8b5cf6]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-white font-semibold">{t('authForgotPassword.securityCode')}</h3>
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full font-medium">
                              {t('authForgotPassword.recommended')}
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm">
                            {t('authForgotPassword.receive6Digit')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </label>

                  {/* Email Link Option */}
                  <label className="cursor-pointer block">
                    <input
                      type="radio"
                      name="resetMethod"
                      value="email-link"
                      checked={resetMethod === 'email-link'}
                      onChange={(e) => setResetMethod(e.target.value as ResetMethod)}
                      className="sr-only"
                    />
                    <div className={`p-4 rounded-lg border-2 transition-all ${
                      resetMethod === 'email-link' 
                        ? 'border-[#8b5cf6] bg-[#8b5cf6]/10' 
                        : 'border-gray-700 bg-[#1a1f35] hover:border-gray-600'
                    }`}>
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold mb-1">Email Link</h3>
                          <p className="text-gray-400 text-sm mb-2">
                            Receive a clickable link in your email
                          </p>
                          <div className="flex items-start space-x-2 bg-yellow-500/10 border border-yellow-500/20 rounded p-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                            <p className="text-yellow-400 text-xs">
                              Link expires in 10 seconds. Click immediately!
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>
                </div>

                <button
                  onClick={() => setCurrentStep('email-input')}
                  className="w-full bg-[#5F8568] hover:bg-[#547a5d] text-white font-medium py-3 px-6 rounded-lg transition-colors"
                >
                  {t('authForgotPassword.continue')}
                </button>
              </motion.div>
            )}

            {/* Step 2: Email Input */}
            {currentStep === 'email-input' && (
              <motion.div
                key="email-input"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-white mb-2">
                    {resetMethod === 'email-link' ? t('authForgotPassword.sendResetLinkHeading') : t('authForgotPassword.sendSecurityCodeHeading')}
                  </h2>
                  <p className="text-gray-400">
                    {resetMethod === 'email-link' 
                      ? t('authForgotPassword.sendResetDesc') 
                      : t('authForgotPassword.sendCodeDesc')}
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm mb-4"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  </motion.div>
                )}

                {/* Success Message */}
                {success && (
                  <motion.div
                    className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm mb-4"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{success}</span>
                    </div>
                  </motion.div>
                )}

                {/* Email Form */}
                <form onSubmit={handleEmailSubmit} className="space-y-4 mb-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      {t('authForgotPassword.enterEmailLabel')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-transparent border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-600 text-base transition-colors"
                      placeholder={t('authForgotPassword.enterEmailPlaceholder')}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center py-3 px-6 bg-[#5F8568] hover:bg-[#547a5d] text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        <span>{t('authForgotPassword.sending')}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        <span>
                          {resetMethod === 'email-link' ? t('views.forgotPassword.sendResetLink') : t('views.forgotPassword.sendSecurityCode')}
                        </span>
                      </>
                    )}
                  </button>
                </form>

                <button
                  onClick={() => setCurrentStep('method-selection')}
                  className="text-gray-400 hover:text-gray-300 font-medium text-sm flex items-center justify-center w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  {t('views.forgotPassword.backToMethod')}
                </button>
              </motion.div>
            )}

            {/* Step 3: OTP Input */}
            {currentStep === 'otp-input' && (
              <motion.div
                key="otp-input"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-white mb-2">
                    {t('authForgotPassword.enterSecurityCode')}
                  </h2>
                  <p className="text-gray-400">
                    {t('authForgotPassword.codeSentTo')} <span className="text-[#8b5cf6]">{email}</span>
                  </p>
                </div>

                {/* Timer */}
                {otpTimeLeft > 0 && (
                  <div className="bg-[#1a1f35] p-3 rounded-lg border border-gray-700 text-center mb-4">
                    <div className="flex items-center justify-center space-x-2">
                      <Clock className="w-4 h-4 text-[#8b5cf6]" />
                      <span className="text-[#8b5cf6] font-medium">
                        {t('authForgotPassword.codeExpiresIn', { time: formatTime(otpTimeLeft) })}
                      </span>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <motion.div
                    className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm mb-4"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  </motion.div>
                )}

                {/* Success Message */}
                {success && (
                  <motion.div
                    className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm mb-4"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{success}</span>
                    </div>
                  </motion.div>
                )}

                {/* OTP Form */}
                <form onSubmit={handleOtpSubmit} className="space-y-4 mb-6">
                  <div>
                    <label htmlFor="otpCode" className="block text-sm font-medium text-gray-300 mb-2">
                      {t('authForgotPassword.sixDigitLabel')}
                    </label>
                    <input
                      type="text"
                      id="otpCode"
                      value={otpCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtpCode(value);
                        setError(null);
                      }}
                      className="w-full px-4 py-3 bg-transparent border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-600 text-center text-lg font-mono tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      {t('authForgotPassword.enterCodeHint')}
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otpCode.length !== 6 || otpTimeLeft === 0}
                    className="w-full flex items-center justify-center py-3 px-6 bg-[#5F8568] hover:bg-[#547a5d] text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        <span>{t('authForgotPassword.verifying')}</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        <span>{t('authForgotPassword.verifyCode')}</span>
                      </>
                    )}
                  </button>
                </form>

                {/* Resend OTP */}
                <div className="space-y-3">
                  <button
                    onClick={handleResendOtp}
                    disabled={loading || otpTimeLeft > 240}
                    className="text-[#8b5cf6] hover:text-[#7c3aed] font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed w-full"
                  >
                    {t('authForgotPassword.didntReceiveResend')}
                  </button>
                  
                  <button
                    onClick={resetFlow}
                    className="text-gray-400 hover:text-gray-300 font-medium text-sm flex items-center justify-center w-full"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    {t('authForgotPassword.tryDifferentMethod')}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Success */}
            {currentStep === 'success' && (
              <motion.div
                key="success"
                className="text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-8 mb-6">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-3">{t('authForgotPassword.checkYourEmail')}</h3>
                  <p className="text-green-400 mb-6">
                    {success}
                  </p>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() => onNavigate?.('login')}
                      className="w-full bg-green-500 hover:bg-green-400 text-gray-900 font-medium py-3 px-6 rounded-lg transition-colors"
                    >
                      {t('authForgotPassword.returnToLogin')}
                    </button>
                    
                    <button
                      onClick={() => window.open('https://mail.google.com', '_blank')}
                      className="w-full bg-[#5F8568] hover:bg-[#547a5d] text-white font-medium py-3 px-6 rounded-lg transition-colors"
                    >
                      Open Email
                    </button>
                    
                    <button
                      onClick={resetFlow}
                      className="w-full bg-[#1a1f35] hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-colors border border-gray-700"
                    >
                      {t('views.forgotPassword.sendAnotherReset')}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Back to Login */}
          {currentStep !== 'success' && (
            <motion.div
              className="text-center mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <button
                onClick={() => onNavigate?.('login')}
                className="text-gray-400 hover:text-gray-300 font-medium text-sm flex items-center justify-center w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('views.forgotPassword.backToLogin')}
              </button>
            </motion.div>
          )}

          {/* Privacy Policy & Terms Footer */}
          <div className="text-center mt-8 pt-6 border-t border-gray-700">
            <p className="text-xs text-gray-500">
              <button
                onClick={() => window.open('/privacy-policy', '_blank', 'noopener,noreferrer')}
                className="hover:text-gray-400 transition-colors"
              >
                {t('footer.privacyPolicy')}
              </button>
              {' • '}
              <button
                onClick={() => window.open('/terms-of-service', '_blank', 'noopener,noreferrer')}
                className="hover:text-gray-400 transition-colors"
              >
                {t('footer.termsAndConditions')}
              </button>
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {t('views.contact.allRightsReserved')}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
